import { Affix, Button, Form, Input, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import ModalDiscardChanges from '../../../../components/Shared/modal/discardChanges';
import ModalConfirm from '../../../../components/Shared/modal/confirm';
import { useEffect, useState } from 'react';
import { useBlocker } from '../../../../util/usePrompt';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTER } from '../../../../constants/router';
import useNotify from '../../../../context/Notification/useNotify';
import DataFlowService from '../../../../services/DataFlow.service';
import { useCallback } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;

export default function Tab8({ getDataFlowDetail, dcDpo }) {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { businessProcessId } = useParams();

    const notify = useNotify();

    // Confirm
    const [openConfirm, setOpenConfirm] = useState(false);
    const [valueSave, setValueSave] = useState();
    const [loading, setLoading] = useState(false);

    // Discard change
    const [change, setChange] = useState(false);
    const [openChange, setOpenChange] = useState(false);
    const [nextLocation, setNextLocation] = useState(null);
    const [isChangeLocation, setIsChangeLocation] = useState(false);

    useBlocker((nextLocation) => onChangePage(nextLocation), change === true);

    const onChangePage = (nextLocation) => {
        setOpenChange(true);
        setIsChangeLocation(true);
        setNextLocation(nextLocation);
    };

    const handelOk = async () => {
        setOpenChange(false);
        setChange(false);
    };

    const handelOkLocation = async () => {
        setOpenChange(false);
        setChange(false);
        setIsChangeLocation(false);
        await form.resetFields();
        await navigate(
            nextLocation.location.pathname + nextLocation.location.search
        );
        await getDataFlowDetail(businessProcessId);
    };

    // Discard change end
    const onCancel = (e) => {
        navigate(
            `${ROUTER.DS_INVENTORY.DATAFLOW}/${businessProcessId}/view?tab=7&mode=view`
        );
    };

    useEffect(() => {
        form.setFieldsValue({
            nameTh: dcDpo?.nameTh,
            nameEn: '',
            addressTh: dcDpo?.addressTh,
            addressEn: '',
            contractTh: dcDpo?.contractTh,
            contractEn: '',
        });
    }, [dcDpo, form]);

    const service = useCallback(
        (payload) => {
            if (!dcDpo) return DataFlowService.createDcDpo(payload);
            else return DataFlowService.updateDcDpo(payload);
        },
        [dcDpo]
    );

    const onFinish = async () => {
        const payload = {
            businessProcessId,
            dcDpo: { ...valueSave },
        };
        try {
            const { data } = await service(payload);
            const item = data?.resultData;
            if (item) {
                notify({
                    title: t('common.Success'),
                    description: t('common.SaveData'),
                });
                setOpenChange(false);
                setChange(false);
                getDataFlowDetail(businessProcessId);
                setLoading(false);
                setOpenConfirm(false);
                onCancel();
            }
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <div>
            <Form
                scrollToFirstError={{
                    behavior: 'smooth',
                    block: 'center',
                }}
                layout="vertical"
                form={form}
                onFinish={(value) => {
                    setOpenConfirm(true);
                    setValueSave(value);
                }}
                onFieldsChange={(_, allFields) => {
                    if (allFields.length > 0) {
                        setChange(true);
                    }
                }}
            >
                <Affix offsetTop={60}>
                    <div className="flex justify-end space-x-3">
                        <Button
                            size="large"
                            type="default"
                            onClick={onCancel}
                            className="text-purple-PHX border-purple-PHX w-32"
                        >
                            {t('common.Cancel')}
                        </Button>
                        <Button
                            size="large"
                            type="primary"
                            className="w-32"
                            htmlType="submit"
                        >
                            {t('common.Save')}
                        </Button>
                    </div>
                </Affix>
                <Form.Item
                    name="nameTh"
                    label={
                        <>
                            <span className="text-[21px] font-bold">
                                {t('dcdpo.DataController')}
                            </span>
                            <Tooltip
                                placement="right"
                                overlayClassName="min-w-[320px]"
                                title={
                                    <div
                                        className="whitespace-pre-line"
                                        dangerouslySetInnerHTML={{
                                            __html: t(
                                                'dcdpo.DataControllerTooltip'
                                            ),
                                        }}
                                    ></div>
                                }
                            >
                                <a className="bg-purple-light p-2 flex items-center justify-center rounded-lg text-purple ml-2">
                                    <ExclamationCircleOutlined />
                                </a>
                            </Tooltip>
                        </>
                    }
                >
                    <Input
                        size="large"
                        rows={4}
                        placeholder={t('dataFlow.inputpl3')}
                    />
                </Form.Item>
                <Form.Item name="nameEn" hidden />
                <Form.Item
                    name="addressTh"
                    label={
                        <>
                            <span className="text-[21px] font-bold">
                                {t('dcdpo.Address')}
                            </span>
                            <Tooltip
                                placement="right"
                                overlayClassName="min-w-[320px]"
                                title={
                                    <div className="whitespace-pre-line">
                                        {t('dcdpo.AddressTooltip')}
                                    </div>
                                }
                            >
                                <a className="bg-purple-light p-2 flex items-center justify-center rounded-lg text-purple ml-2">
                                    <ExclamationCircleOutlined />
                                </a>
                            </Tooltip>
                        </>
                    }
                >
                    <TextArea
                        size="large"
                        rows={4}
                        placeholder={t('dataFlow.inputpl3')}
                    />
                </Form.Item>
                <Form.Item name="addressEn" hidden />
                <Form.Item
                    name="contractTh"
                    label={
                        <>
                            <span className="text-[21px] font-bold">
                                {t('dcdpo.Contact')}
                            </span>
                            <Tooltip
                                placement="right"
                                overlayClassName="min-w-[320px]"
                                title={
                                    <div
                                        className="whitespace-pre-line"
                                        dangerouslySetInnerHTML={{
                                            __html: t('dcdpo.ContactTooltip'),
                                        }}
                                    ></div>
                                }
                            >
                                <a className="bg-purple-light p-2 flex items-center justify-center rounded-lg text-purple ml-2">
                                    <ExclamationCircleOutlined />
                                </a>
                            </Tooltip>
                        </>
                    }
                >
                    <TextArea
                        size="large"
                        rows={4}
                        placeholder={t('dataFlow.inputpl3')}
                    />
                </Form.Item>
                <Form.Item name="contractEn" hidden />
            </Form>
            <ModalDiscardChanges
                open={openChange}
                handelOk={isChangeLocation ? handelOkLocation : handelOk}
                handelClose={() => setOpenChange(false)}
            />
            <ModalConfirm
                loading={loading}
                text={t('Do you want to confirm')}
                open={openConfirm}
                handelOk={onFinish}
                handelClose={() => setOpenConfirm(false)}
            />
        </div>
    );
}
